import { Injectable, inject } from '@angular/core';

import { Store } from '@ngrx/store';
import type { Observable } from 'rxjs';
import {
  distinctUntilChanged,
  EMPTY,
  filter,
  startWith,
  Subject,
  switchMap,
  takeUntil,
} from 'rxjs';
import { map } from 'rxjs/operators';

import { AccountSelectors } from '@core/account-data';
import { filterBoolean } from '@core/common-legacy';
import { injectSubscriptionStatusContract } from '@core/company-contracts';
import type { Company } from '@core/company-types';
import { SpamVerificationActions, SpamVerificationSelectors } from '@core/spam-verification-data';
import { CompanyVerificationRequestStatus } from '@core/spam-verification-types';
import { DialogService } from '@core/user-interface-legacy';

@Injectable({ providedIn: 'root' })
export class SpamVerificationService {
  private readonly store = inject(Store);
  private readonly dialogService = inject(DialogService);

  private readonly subscription = injectSubscriptionStatusContract();

  public selectSpamVerificationStatus$ = this.store
    .select(SpamVerificationSelectors.selectSpamVerificationStatus)
    .pipe(startWith(CompanyVerificationRequestStatus.Pending), filterBoolean);

  public signupDomainVerifiedAt$: Observable<Company['signupDomainVerifiedAt']> = this.store
    .select(AccountSelectors.currentCompany)
    .pipe(
      filterBoolean,
      map(company => company.signupDomainVerifiedAt),
    );

  public verificationRequired$: Observable<boolean> = this.signupDomainVerifiedAt$.pipe(
    map(() => this.verificationRequired()),
  );

  public spamVerificationNotApproved$ = this.selectSpamVerificationStatus$.pipe(
    switchMap(status =>
      status !== CompanyVerificationRequestStatus.Approved ? this.verificationRequired$ : EMPTY,
    ),
  );

  private ngOnDestroy$: Subject<void> = new Subject<void>();
  private signupDomainVerifiedAt: Company['signupDomainVerifiedAt'];

  constructor() {
    this.signupDomainVerifiedAt$
      .pipe(takeUntil(this.ngOnDestroy$))
      .subscribe(signupDomainVerifiedAt => {
        this.signupDomainVerifiedAt = signupDomainVerifiedAt;
      });

    this.signupDomainVerifiedAt$
      .pipe(
        distinctUntilChanged(),
        filter(() => this.subscription.isTrial()),
        takeUntil(this.ngOnDestroy$),
      )
      .subscribe(() => {
        this.store.dispatch(SpamVerificationActions.fetch());
      });
  }

  public ngOnDestroy(): void {
    this.ngOnDestroy$.next();
    this.ngOnDestroy$.complete();
  }

  public verificationRequired(): boolean {
    const isTrial = this.subscription.isTrial();
    const isExpired = this.subscription.isExpired();
    const isInactive = this.subscription.isInactive();

    const isDomainVerified = !!this.signupDomainVerifiedAt;

    return (isTrial || isExpired || isInactive) && !isDomainVerified;
  }
}
